'use client';
import { useQuery } from '@tanstack/react-query';
import { memo, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { CartContext, CartItemsFieldsContext, type CartFormValues } from './cart-context';
import { CartTotalsSync } from './cart-totals-sync';
import { CartDeliveryMethodSync } from './delivery-method-sync';
import { CART_STORAGE_KEY, useCartFormPersist } from './use-cart-form-persist';

export { CartContext, CART_STORAGE_KEY };
export type { CartFormValues };

type Props = {
    children?: ReactNode;
};

/** Isolates app tree from CartProvider re-renders (useForm / query updates). */
const CartProviderTree = memo(function CartProviderTree({ children }: { children: ReactNode }) {
    return children;
});

export const CartProvider = ({ children }: Props) => {
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const addressesQuery = useQuery(QueryApi.Customer.queries.Addresses({ offset: 0, limit: 100 }));
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const paymentMethodsQuery = useQuery(QueryApi.PaymentMethods.queries.PaymentMethods);
    const menuListsQuery = useQuery(QueryApi.MenuList.queries.MenuLists);
    const prevCustomerData = useRef(customerQuery.data);
    const prevAddressesData = useRef(addressesQuery.data);
    const prevMenuListsDataRef = useRef(menuListsQuery.data);

    const getCartFormDefaultValues = useCallback(
        (prevFormValues?: Partial<CartFormValues>): CartFormValues => {
            const defaultDeliveryMethod = deliveryMethodsQuery.data?.[0] ?? null;
            const defaultDeliveryAddress = addressesQuery.data?.items?.find((a) => a.isDefault);
            const isDeliveryType = defaultDeliveryMethod ? isDeliveryMethod(defaultDeliveryMethod) : false;
            const defaultAddress =
                prevFormValues?.deliveryMethod?.address ??
                (isDeliveryType ? (defaultDeliveryAddress?.address ?? null) : null);
            const defaultAddressId =
                prevFormValues?.deliveryMethod?.addressId ??
                (isDeliveryType ? (defaultDeliveryAddress?.id ?? null) : null);

            return {
                bonusPoints: [0],
                contactInfo: prevFormValues?.contactInfo ?? {
                    name: customerQuery.data?.name ?? '',
                    surname: customerQuery.data?.surname ?? '',
                    email: customerQuery.data?.email ?? '',
                    telephone: customerQuery.data?.telephone
                        ? `${customerQuery.data.telephonePrefix} ${customerQuery.data.telephone}`
                        : '',
                    telephoneVerified: false,
                },
                addCompanyInfo: prevFormValues?.addCompanyInfo ?? false,
                company: prevFormValues?.company ?? {
                    name: '',
                    address: null,
                    cin: '',
                    vat: '',
                    issueInvoice: false,
                },
                deliveryMethod: prevFormValues?.deliveryMethod ?? {
                    id: defaultDeliveryMethod?.id ?? null,
                    type: defaultDeliveryMethod
                        ? isDeliveryType
                            ? DeliveryType.DELIVERY
                            : DeliveryType.PICKUP
                        : null,
                    address: defaultAddress
                        ? {
                              city: defaultAddress.city,
                              country: defaultAddress.country,
                              lon: defaultAddress.lon,
                              lat: defaultAddress.lat,
                              postCode: defaultAddress.postCode,
                              street: defaultAddress.street,
                          }
                        : null,
                    addressId: defaultAddressId,
                    manual: !customerQuery.data,
                },
                paymentMethod: prevFormValues?.paymentMethod ?? {
                    id: paymentMethodsQuery.data?.[0]?.id ?? null,
                },
                time: {
                    type: 'earliest',
                    timed: null,
                },
                deliveryTime: null,
                items: [],
                newsletterAgreement: prevFormValues?.newsletterAgreement ?? false,
                privacyPolicyAgreement: prevFormValues?.privacyPolicyAgreement ?? false,
                note: '',
                tips: {},
                topUpOption: 'pay',
                saveForNextOrder: prevFormValues?.saveForNextOrder ?? false,
                _isHydrated: prevFormValues?._isHydrated ?? false,
                _isOrderCreated: prevFormValues?._isOrderCreated ?? false,
            };
        },
        [addressesQuery.data, customerQuery.data, deliveryMethodsQuery.data, paymentMethodsQuery.data],
    );

    const formMethods = useForm<CartFormValues>({
        defaultValues: getCartFormDefaultValues(),
    });
    const itemsFieldArray = useFieldArray({ name: 'items', control: formMethods.control, keyName: '_id' });
    const itemsFieldArrayRef = useRef(itemsFieldArray);
    itemsFieldArrayRef.current = itemsFieldArray;

    // Stable field-array API so CartContext identity doesn't churn when CartProvider
    // re-renders for unrelated form/query updates (delivery method, tips, …).
    const stableItemsFieldArray = useMemo(() => {
        const current = () => itemsFieldArrayRef.current;
        return {
            get fields() {
                return current().fields;
            },
            append: ((...args) => current().append(...args)) as typeof itemsFieldArray.append,
            prepend: ((...args) => current().prepend(...args)) as typeof itemsFieldArray.prepend,
            insert: ((...args) => current().insert(...args)) as typeof itemsFieldArray.insert,
            swap: ((...args) => current().swap(...args)) as typeof itemsFieldArray.swap,
            move: ((...args) => current().move(...args)) as typeof itemsFieldArray.move,
            update: ((...args) => current().update(...args)) as typeof itemsFieldArray.update,
            replace: ((...args) => current().replace(...args)) as typeof itemsFieldArray.replace,
            remove: ((...args) => current().remove(...args)) as typeof itemsFieldArray.remove,
        } as typeof itemsFieldArray;
        // Methods always delegate through the ref — do not depend on itemsFieldArray identity.
    }, []);

    useCartFormPersist(formMethods);

    useEffect(() => {
        if (customerQuery.data && !prevCustomerData.current) {
            const formValues = formMethods.getValues();
            const isMatch =
                formValues.contactInfo.name === customerQuery.data.name &&
                formValues.contactInfo.surname === customerQuery.data.surname &&
                formValues.contactInfo.email === customerQuery.data.email &&
                formValues.contactInfo.telephone ===
                    `${customerQuery.data.telephonePrefix} ${customerQuery.data.telephone}`;

            if (isMatch) {
                return;
            }

            const isEdited =
                !!formValues.contactInfo.name ||
                !!formValues.contactInfo.surname ||
                !!formValues.contactInfo.email ||
                !!formValues.contactInfo.telephone?.split(' ')?.[1]?.length;

            const handleUpdateData = () => {
                if (!customerQuery.data) {
                    return;
                }
                formMethods.setValue('contactInfo.name', customerQuery.data.name);
                formMethods.setValue('contactInfo.surname', customerQuery.data.surname);
                formMethods.setValue('contactInfo.email', customerQuery.data.email);
                if (customerQuery.data.telephonePrefix && customerQuery.data.telephone) {
                    formMethods.setValue(
                        'contactInfo.telephone',
                        `${customerQuery.data.telephonePrefix} ${customerQuery.data.telephone}`,
                    );
                }
            };

            if (isEdited) {
                ModalService.showPrefab('OrderAutoFillModal', {
                    onConfirm: () => {
                        handleUpdateData();
                    },
                });
            } else {
                handleUpdateData();
            }
        }
        prevCustomerData.current = customerQuery.data;
    }, [customerQuery.data]);

    useEffect(() => {
        if (!customerQuery.data || !addressesQuery.data) {
            return;
        }
        const selectedAddressId = formMethods.getValues('deliveryMethod.addressId');
        const selectedAddress = addressesQuery.data?.items.find((a) => a.id === selectedAddressId);
        if (selectedAddressId && !selectedAddress) {
            const newAddress =
                addressesQuery.data.items?.find((a) => a.isDefault) ?? addressesQuery.data.items?.[0] ?? null;
            if (newAddress) {
                formMethods.setValue('deliveryMethod.addressId', newAddress.id);
                formMethods.setValue('deliveryMethod.address', {
                    city: newAddress.address.city,
                    country: newAddress.address.country,
                    lat: newAddress.address.lat,
                    lon: newAddress.address.lon,
                    postCode: newAddress.address.postCode,
                    street: newAddress.address.street,
                });
            } else {
                formMethods.setValue('deliveryMethod.addressId', null);
                formMethods.setValue('deliveryMethod.address', null);
            }
        }
    }, [addressesQuery.data]);

    useEffect(() => {
        const formValues = formMethods.getValues();
        if (addressesQuery.data && !prevAddressesData.current) {
            const isAddressEntered = !!formValues.deliveryMethod.address?.street;
            const address = formValues.deliveryMethod.address;
            const allAddresses = addressesQuery.data?.items ?? [];
            const matchingAddress = isAddressEntered
                ? allAddresses.find(
                      (a) =>
                          a.address.city === address?.city &&
                          a.address.country === address?.country &&
                          a.address.lat === address?.lat &&
                          a.address.lon === address?.lon &&
                          a.address.postCode === address?.postCode &&
                          a.address.street === address?.street,
                  )
                : undefined;
            const foundAddress = matchingAddress ?? allAddresses.find((a) => a.isDefault) ?? allAddresses[0];

            if (matchingAddress || (!isAddressEntered && foundAddress)) {
                formMethods.setValue('deliveryMethod.addressId', foundAddress.id);
                formMethods.setValue('deliveryMethod.address', {
                    city: foundAddress.address.city,
                    country: foundAddress.address.country,
                    postCode: foundAddress.address.postCode,
                    street: foundAddress.address.street,
                    lat: foundAddress.address.lat,
                    lon: foundAddress.address.lon,
                });
            }
            if (matchingAddress || !isAddressEntered) {
                formMethods.setValue('deliveryMethod.manual', false);
            }
        } else if (!addressesQuery.data && prevAddressesData.current) {
            formMethods.setValue('deliveryMethod.addressId', null);
            formMethods.setValue('deliveryMethod.manual', true);
        }
        prevAddressesData.current = addressesQuery.data;
    }, [addressesQuery.data]);

    // Fallback validation: remove cart items that no longer exist in menu
    // This handles cases where socket event was missed (e.g., page reload, reconnection)
    useEffect(() => {
        const isHydrated = formMethods.getValues('_isHydrated');
        if (!menuListsQuery.data || !isHydrated) return;

        // Only run validation when menu data actually changes (not on initial load)
        const prevData = prevMenuListsDataRef.current;
        prevMenuListsDataRef.current = menuListsQuery.data;
        if (!prevData) return;

        const validHashes = new Set(Object.keys(menuListsQuery.data.items));
        const currentItems = itemsFieldArrayRef.current.fields;

        // Find indices of items that no longer exist in menu
        const indicesToRemove = currentItems
            .map((field, index) => {
                const itemExists = validHashes.has(field.hash);
                const variantExists = !field.variant || validHashes.has(field.variant);
                return itemExists && variantExists ? -1 : index;
            })
            .filter((index) => index !== -1)
            .reverse(); // Remove from end to preserve indices

        if (indicesToRemove.length > 0) {
            indicesToRemove.forEach((index) => {
                itemsFieldArrayRef.current.remove(index);
            });
        }
    }, [menuListsQuery.data, formMethods]);

    const contextValue = useMemo(
        () => ({
            formMethods,
            getCartFormDefaultValues,
            // Stable API with fields getter — do not recreate on quantity/note edits
            // (that would re-render every CartContext consumer). Lists subscribe to
            // CartItemsFieldsContext; counts use useCartItems (useWatch).
            itemsFieldArray: stableItemsFieldArray,
        }),
        [formMethods, getCartFormDefaultValues, stableItemsFieldArray],
    );

    return (
        <CartContext.Provider value={contextValue}>
            <CartItemsFieldsContext.Provider value={itemsFieldArray.fields}>
                <CartDeliveryMethodSync />
                <CartTotalsSync />
                <CartProviderTree>{children}</CartProviderTree>
            </CartItemsFieldsContext.Provider>
        </CartContext.Provider>
    );
};
